import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit3, ShoppingBag, ExternalLink, Upload, ArchiveRestore, Archive, CheckCircle2, X, Search, Star, Loader2, FileText, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { PrintifyPanel } from "./AdminPrintify";
import { toast } from "sonner";

const CATEGORIES = ["Music", "Books", "Digital Resources", "Merchandise"];
const TYPES = [
  { v: "DIGITAL_DOWNLOAD", label: "Digital download (via Temple checkout)" },
  { v: "EXTERNAL_PRODUCT", label: "External product (Amazon, etc.)" },
];

const inputCls = "w-full rounded-lg border px-3 py-2 text-sm bg-transparent";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest mb-1";

const Field = ({ label, children, hint }) => (
  <label className="block mb-4">
    <span className={labelCls} style={{ color: "var(--gold-dark)" }}>{label}</span>
    {children}
    {hint && <span className="block text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{hint}</span>}
  </label>
);

const StatusBadge = ({ status }) => {
  const map = {
    published: { bg: "var(--gold)", fg: "var(--burgundy)" },
    draft: { bg: "var(--bg-cream)", fg: "var(--text-muted)" },
    archived: { bg: "var(--dark-card)", fg: "var(--bg-cream)" },
  };
  const c = map[status] || map.draft;
  return <span className="rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest" style={{ background: c.bg, color: c.fg }}>{status}</span>;
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (image_id) => image_id ? `${BACKEND}/api/store/images/${image_id}` : null;

function formatUSD(cents, currency = "usd") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format((cents || 0) / 100);
}

export function StorePanel() {
  const [sub, setSub] = useState("products"); // products | orders | legal
  return (
    <div>
      <div className="flex items-center gap-2 mb-6" data-testid="admin-store-subnav">
        {[
          { id: "products", label: "Products" },
          { id: "orders", label: "Orders" },
          { id: "printify", label: "Printify" },
          { id: "legal", label: "Legal Pages" },
        ].map((s) => (
          <button
            key={s.id}
            data-testid={`admin-store-sub-${s.id}-btn`}
            onClick={() => setSub(s.id)}
            className="px-3 py-1.5 rounded-full text-xs font-medium"
            style={{
              background: sub === s.id ? "var(--burgundy)" : "var(--bg-cream-card)",
              color: sub === s.id ? "var(--bg-cream)" : "var(--text-obsidian)",
              border: `1px solid ${sub === s.id ? "var(--burgundy)" : "var(--border-cream)"}`,
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === "products" && <ProductsPanel />}
      {sub === "orders" && <OrdersPanel />}
      {sub === "printify" && <PrintifyPanel />}
      {sub === "legal" && <LegalPanel />}
    </div>
  );
}

function ProductsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);   // product object or "new"
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/store/products?${params.toString()}`);
      setItems(data || []);
    } catch { toast.error("Could not load products"); }
    finally { setLoading(false); }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const doPublish = async (p) => {
    try {
      await api.post(`/admin/store/products/${p.id}/publish`);
      toast.success("Published");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot publish"); }
  };
  const doArchive = async (p) => {
    try { await api.post(`/admin/store/products/${p.id}/archive`); toast("Archived"); load(); }
    catch { toast.error("Could not archive"); }
  };
  const doUnarchive = async (p) => {
    try { await api.post(`/admin/store/products/${p.id}/unarchive`); toast("Moved to Draft"); load(); }
    catch { toast.error("Could not restore"); }
  };
  const doDelete = async (p) => {
    if (!window.confirm(`DELETE PERMANENTLY "${p.title}"?\n\nThis is not the same as Archive. The product record and its files are removed and cannot be recovered. Products with purchases, download entitlements, or merch orders are protected and will be refused.`)) return;
    try { await api.delete(`/admin/store/products/${p.id}`); toast("Product deleted permanently"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete", { duration: 12000 }); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>Store · Products</h2>
        <button
          data-testid="admin-store-new-product-btn"
          onClick={() => setEditing("new")}
          className="ml-auto inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
          style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
        >
          <Plus className="w-4 h-4" /> New product
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["", "draft", "published", "archived"].map((s) => (
          <button
            key={s || "all"}
            data-testid={`admin-store-filter-${s || "all"}-btn`}
            onClick={() => setStatusFilter(s)}
            className="px-3 py-1 rounded-full text-xs"
            style={{
              background: statusFilter === s ? "var(--burgundy)" : "transparent",
              color: statusFilter === s ? "var(--bg-cream)" : "var(--text-obsidian)",
              border: `1px solid ${statusFilter === s ? "var(--burgundy)" : "var(--border-cream)"}`,
            }}
          >
            {s || "All"}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
          <input
            data-testid="admin-store-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title…"
            className="rounded-full border pl-8 pr-3 py-1.5 text-xs bg-transparent"
            style={{ borderColor: "var(--border-cream)" }}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16" style={{ color: "var(--text-muted)" }}>
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border p-10 text-center text-sm" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }} data-testid="admin-store-empty">
          No products yet. Click <strong>New product</strong> to create one.
        </div>
      ) : (
        <div className="rounded-2xl border overflow-x-auto" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "var(--border-cream)", color: "var(--gold-dark)" }}>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Product</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Category</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Type</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Price</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b last:border-b-0" style={{ borderColor: "var(--border-cream)" }} data-testid={`admin-store-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image_id ? (
                        <img src={imgUrl(p.image_id)} alt="" className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: "var(--burgundy-tint)", color: "var(--burgundy)" }}><ShoppingBag className="w-4 h-4" /></div>
                      )}
                      <div className="min-w-0">
                        <div className="font-serif truncate" style={{ color: "var(--burgundy)" }}>
                          {p.title} {p.featured && <Star className="inline w-3 h-3 ml-1" style={{ color: "var(--gold-dark)" }} />}
                        </div>
                        {p.subtitle && <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{p.subtitle}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{p.category}</td>
                  <td className="px-4 py-3 text-xs">{p.product_type === "DIGITAL_DOWNLOAD" ? "Digital" : "External"}</td>
                  <td className="px-4 py-3 text-xs">{p.product_type === "EXTERNAL_PRODUCT" ? "—" : formatUSD(p.price_cents, p.currency)}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`admin-store-edit-${p.id}-btn`} onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-[color:var(--burgundy-tint)]"><Edit3 className="w-3.5 h-3.5" /></button>
                      {p.status === "draft" && (
                        <button data-testid={`admin-store-publish-${p.id}-btn`} onClick={() => doPublish(p)} className="p-1.5 rounded hover:bg-[color:var(--burgundy-tint)]" title="Publish"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--burgundy)" }} /></button>
                      )}
                      {p.status !== "archived" && (
                        <button data-testid={`admin-store-archive-${p.id}-btn`} onClick={() => doArchive(p)} className="p-1.5 rounded hover:bg-[color:var(--burgundy-tint)]" title="Archive"><Archive className="w-3.5 h-3.5" /></button>
                      )}
                      {p.status === "archived" && (
                        <button data-testid={`admin-store-unarchive-${p.id}-btn`} onClick={() => doUnarchive(p)} className="p-1.5 rounded hover:bg-[color:var(--burgundy-tint)]" title="Restore to draft"><ArchiveRestore className="w-3.5 h-3.5" /></button>
                      )}
                      <button data-testid={`admin-store-delete-${p.id}-btn`} onClick={() => doDelete(p)} className="p-1.5 rounded hover:bg-[color:var(--rose)]" title="Delete permanently"><Trash2 className="w-3.5 h-3.5" style={{ color: "var(--rose-dark)" }} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ProductEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(initial || {
    title: "", subtitle: "", description: "",
    category: "Digital Resources", product_type: "DIGITAL_DOWNLOAD",
    price_cents: 0, currency: "usd", external_url: "", featured: false, position: 0,
  });
  const [product, setProduct] = useState(initial); // populated once created
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingPreview, setUploadingPreview] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadPreview = async (file) => {
    if (!product) return toast.error("Save the product first.");
    setUploadingPreview(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/store/products/${product.id}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProduct((p) => ({ ...p, preview_id: data.preview_id, preview_url: data.preview_url }));
      upd("preview_url", data.preview_url);
      toast.success("Preview clip uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview upload failed"); }
    finally { setUploadingPreview(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew && !product) {
        const payload = { ...form, price_cents: form.product_type === "DIGITAL_DOWNLOAD" ? Number(form.price_cents) || 0 : null };
        const { data } = await api.post("/admin/store/products", payload);
        setProduct(data);
        toast.success("Product created as draft. You can now upload an image and file.");
      } else {
        const pid = (product || initial).id;
        const patch = { ...form };
        // Only send changed simple types (server ignores unset)
        const { data } = await api.patch(`/admin/store/products/${pid}`, patch);
        setProduct(data);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!product) return toast.error("Save the product first.");
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/store/products/${product.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProduct((p) => ({ ...p, image_id: data.image_id }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image upload failed"); }
    finally { setUploadingImage(false); }
  };

  const uploadFile = async (file) => {
    if (!product) return toast.error("Save the product first.");
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/store/products/${product.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProduct((p) => ({ ...p, file_id: data.file_id, file_original_name: data.original_name, file_size_bytes: data.size_bytes }));
      toast.success("File uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "File upload failed"); }
    finally { setUploadingFile(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(48,43,41,0.55)" }} onClick={onClose}>
      <div className="w-full max-w-2xl h-full overflow-y-auto p-6 sm:p-8" onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-cream)" }} data-testid="admin-store-editor-drawer">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>{isNew && !product ? "New product" : "Edit product"}</h2>
          <button onClick={onClose} data-testid="admin-store-editor-close-btn" className="p-2 rounded-full hover:bg-[color:var(--burgundy-tint)]"><X className="w-4 h-4" /></button>
        </div>

        <Field label="Title">
          <input data-testid="admin-store-title-input" className={inputCls} value={form.title || ""} onChange={(e) => upd("title", e.target.value)} style={{ borderColor: "var(--border-cream)" }} />
        </Field>
        <Field label="Subtitle" hint="One-line poetic caption shown under the title.">
          <input data-testid="admin-store-subtitle-input" className={inputCls} value={form.subtitle || ""} onChange={(e) => upd("subtitle", e.target.value)} style={{ borderColor: "var(--border-cream)" }} />
        </Field>
        <Field label="Description">
          <textarea data-testid="admin-store-description-input" rows={4} className={inputCls} value={form.description || ""} onChange={(e) => upd("description", e.target.value)} style={{ borderColor: "var(--border-cream)", resize: "vertical" }} />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Category">
            <select data-testid="admin-store-category-select" className={inputCls} value={form.category} onChange={(e) => upd("category", e.target.value)} style={{ borderColor: "var(--border-cream)" }}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Type">
            <select data-testid="admin-store-type-select" className={inputCls} value={form.product_type} onChange={(e) => upd("product_type", e.target.value)} style={{ borderColor: "var(--border-cream)" }}>
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        {form.product_type === "DIGITAL_DOWNLOAD" ? (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Price (USD cents)" hint="e.g. 1200 = $12.00">
              <input data-testid="admin-store-price-input" type="number" min="0" className={inputCls} value={form.price_cents || 0} onChange={(e) => upd("price_cents", parseInt(e.target.value || "0", 10))} style={{ borderColor: "var(--border-cream)" }} />
            </Field>
            <Field label="Currency">
              <input data-testid="admin-store-currency-input" className={inputCls} value={form.currency || "usd"} onChange={(e) => upd("currency", e.target.value.toLowerCase())} style={{ borderColor: "var(--border-cream)" }} maxLength={3} />
            </Field>
          </div>
        ) : (
          <Field label="External purchase URL" hint="Where customers go to buy this — e.g. an Amazon product page.">
            <input data-testid="admin-store-external-url-input" className={inputCls} value={form.external_url || ""} onChange={(e) => upd("external_url", e.target.value)} placeholder="https://amazon.com/…" style={{ borderColor: "var(--border-cream)" }} />
          </Field>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Position" hint="Lower = shown first">
            <input data-testid="admin-store-position-input" type="number" className={inputCls} value={form.position || 0} onChange={(e) => upd("position", parseInt(e.target.value || "0", 10))} style={{ borderColor: "var(--border-cream)" }} />
          </Field>
          <label className="flex items-end gap-2 mb-4">
            <input data-testid="admin-store-featured-checkbox" type="checkbox" checked={!!form.featured} onChange={(e) => upd("featured", e.target.checked)} />
            <span className="text-sm" style={{ color: "var(--text-obsidian)" }}>Feature this product</span>
          </label>
        </div>

        <button
          data-testid="admin-store-save-btn"
          onClick={save}
          disabled={saving}
          className="rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60"
          style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
        >
          {saving ? "Saving…" : (isNew && !product ? "Create draft" : "Save changes")}
        </button>

        {product && (
          <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--border-cream)" }}>
            <div className="eyebrow mb-3">Assets</div>

            <div className="rounded-xl border p-4 mb-4" style={{ borderColor: "var(--border-cream)" }} data-testid="admin-store-image-section">
              <div className="flex items-center gap-4">
                {product.image_id ? (
                  <img src={imgUrl(product.image_id)} alt="" className="w-20 h-20 rounded object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded flex items-center justify-center" style={{ background: "var(--burgundy-tint)", color: "var(--burgundy)" }}>
                    <ShoppingBag className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: "var(--burgundy)" }}>Product image</div>
                  <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>JPG / PNG / WEBP · up to 5 MB</div>
                </div>
                <label className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium cursor-pointer" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }} data-testid="admin-store-image-upload-label">
                  <Upload className="w-3.5 h-3.5" /> {uploadingImage ? "Uploading…" : "Upload image"}
                  <input data-testid="admin-store-image-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                </label>
              </div>
            </div>

            {product.product_type === "DIGITAL_DOWNLOAD" && (
              <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-cream)" }} data-testid="admin-store-file-section">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded flex items-center justify-center" style={{ background: "var(--burgundy-tint)", color: "var(--burgundy)" }}>
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium" style={{ color: "var(--burgundy)" }}>Digital product file</div>
                    {product.file_original_name ? (
                      <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }} data-testid="admin-store-file-name">
                        {product.file_original_name} · {((product.file_size_bytes || 0) / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    ) : (
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>PDF / MP3 / WAV / ZIP · up to 200 MB · stored privately</div>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium cursor-pointer" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }} data-testid="admin-store-file-upload-label">
                    <Upload className="w-3.5 h-3.5" /> {uploadingFile ? "Uploading…" : (product.file_original_name ? "Replace file" : "Upload file")}
                    <input data-testid="admin-store-file-upload-input" type="file" accept=".pdf,.mp3,.wav,.zip,.epub,.m4a,.flac" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
                  </label>
                </div>
              </div>
            )}

            {product.category === "Music" && (
              <div className="mt-4 rounded-xl border p-4" style={{ borderColor: "var(--gold)" }} data-testid="admin-store-preview-block">
                <div className="text-sm font-medium" style={{ color: "var(--burgundy)" }}>Public preview audio (Listen → Music)</div>
                <div className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>
                  A short public clip streamed on the Listen page. This is separate from the purchased file, which stays private.
                </div>
                {product.preview_url ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <audio controls src={product.preview_url.startsWith("/") ? `${BACKEND}${product.preview_url}` : product.preview_url} className="h-9 max-w-xs" data-testid="admin-store-preview-audio" />
                    <button data-testid="admin-store-preview-remove-btn" onClick={async () => {
                      try { await api.delete(`/admin/store/products/${product.id}/preview`); setProduct((p) => ({ ...p, preview_url: null, preview_id: null })); upd("preview_url", ""); toast.success("Preview removed"); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Could not remove preview"); }
                    }} className="rounded-full px-3 py-1.5 text-xs border" style={{ borderColor: "var(--rose-dark)", color: "var(--rose-dark)" }}>Remove preview</button>
                  </div>
                ) : (
                  <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>No preview yet — the album shows on Listen → Music with a Store link only.</div>
                )}
                <label className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium cursor-pointer" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }} data-testid="admin-store-preview-upload-label">
                  <Upload className="w-3.5 h-3.5" /> {uploadingPreview ? "Uploading…" : (product.preview_url ? "Replace preview clip" : "Upload preview clip")}
                  <input data-testid="admin-store-preview-upload-input" type="file" accept=".mp3,.m4a,.wav,.ogg" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPreview(e.target.files[0])} />
                </label>
              </div>
            )}

            <div className="mt-6 flex items-center gap-2">
              {product.status === "draft" && (
                <button
                  data-testid="admin-store-editor-publish-btn"
                  onClick={async () => {
                    try { await api.post(`/admin/store/products/${product.id}/publish`); toast.success("Published"); onSaved(); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Cannot publish"); }
                  }}
                  className="rounded-full px-4 py-2 text-xs font-medium"
                  style={{ background: "var(--gold)", color: "var(--burgundy)" }}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Publish
                </button>
              )}
              <button
                onClick={onSaved}
                className="rounded-full px-4 py-2 text-xs font-medium border"
                style={{ borderColor: "var(--border-cream)", color: "var(--text-obsidian)" }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      const { data } = await api.get(`/admin/store/orders?${params.toString()}`);
      setOrders(data || []);
    } catch { toast.error("Could not load orders"); }
    finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>Store · Orders</h2>
        <div className="ml-auto flex items-center gap-2">
          {["", "paid", "pending", "failed", "refunded", "expired"].map((s) => (
            <button
              key={s || "all"}
              data-testid={`admin-orders-filter-${s || "all"}-btn`}
              onClick={() => setStatus(s)}
              className="px-3 py-1 rounded-full text-[11px]"
              style={{
                background: status === s ? "var(--burgundy)" : "transparent",
                color: status === s ? "var(--bg-cream)" : "var(--text-obsidian)",
                border: `1px solid ${status === s ? "var(--burgundy)" : "var(--border-cream)"}`,
              }}
            >
              {s || "All"}
            </button>
          ))}
        </div>
      </div>
      <div className="relative mb-4">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
        <input
          data-testid="admin-orders-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, product, or session…"
          className="w-full max-w-sm rounded-full border pl-8 pr-3 py-1.5 text-xs bg-transparent"
          style={{ borderColor: "var(--border-cream)" }}
        />
      </div>

      {loading ? (
        <div className="text-center py-16" style={{ color: "var(--text-muted)" }}>
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border p-10 text-center text-sm" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }} data-testid="admin-orders-empty">
          No transactions yet.
        </div>
      ) : (
        <div className="rounded-2xl border overflow-x-auto" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "var(--border-cream)", color: "var(--gold-dark)" }}>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Date</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Member</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Product</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Amount</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Status</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Fulfilled</th>
                <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Stripe ref</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.session_id} className="border-b last:border-b-0" style={{ borderColor: "var(--border-cream)" }} data-testid={`admin-orders-row-${o.session_id}`}>
                  <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-medium" style={{ color: "var(--burgundy)" }}>{o.member_name || "—"}</div>
                    <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{o.user_email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{o.product_name || o.lookup_key}</td>
                  <td className="px-4 py-3 text-xs">{formatUSD(o.amount_cents, o.currency)}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest" style={{
                      background: o.payment_status === "paid" ? "var(--gold)" : "var(--bg-cream)",
                      color: o.payment_status === "paid" ? "var(--burgundy)" : "var(--text-muted)",
                    }}>{o.payment_status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">{o.fulfilled ? "Yes" : "—"}</td>
                  <td className="px-4 py-3 text-[10px] font-mono truncate max-w-[160px]" style={{ color: "var(--text-muted)" }} title={o.session_id}>{o.session_id.slice(0, 24)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LegalPanel() {
  const [slug, setSlug] = useState("privacy");
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/store/legal/${slug}`); setDoc(data); }
    catch { toast.error("Could not load legal page"); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/store/legal/${slug}`, {
        title: doc.title, body: doc.body, is_draft: doc.is_draft,
      });
      setDoc(data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl mb-6" style={{ color: "var(--burgundy)" }}>Store · Legal Pages</h2>
      <div className="flex items-center gap-2 mb-4">
        {[
          { s: "privacy", l: "Privacy" },
          { s: "terms", l: "Terms" },
          { s: "refunds", l: "Refunds" },
        ].map((x) => (
          <button
            key={x.s}
            data-testid={`admin-legal-tab-${x.s}-btn`}
            onClick={() => setSlug(x.s)}
            className="px-3 py-1.5 rounded-full text-xs"
            style={{
              background: slug === x.s ? "var(--burgundy)" : "transparent",
              color: slug === x.s ? "var(--bg-cream)" : "var(--text-obsidian)",
              border: `1px solid ${slug === x.s ? "var(--burgundy)" : "var(--border-cream)"}`,
            }}
          >
            {x.l}
          </button>
        ))}
      </div>

      {!doc ? (
        <div className="text-center py-12" style={{ color: "var(--text-muted)" }}><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
      ) : (
        <div className="rounded-2xl border p-6" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
          <Field label="Title">
            <input data-testid="admin-legal-title-input" className={inputCls} value={doc.title || ""} onChange={(e) => setDoc({ ...doc, title: e.target.value })} style={{ borderColor: "var(--border-cream)" }} />
          </Field>
          <label className="flex items-center gap-2 mb-4">
            <input data-testid="admin-legal-draft-checkbox" type="checkbox" checked={!!doc.is_draft} onChange={(e) => setDoc({ ...doc, is_draft: e.target.checked })} />
            <span className="text-sm" style={{ color: "var(--text-obsidian)" }}>Draft (show warning banner on the public page)</span>
          </label>
          <Field label="Body" hint="Plain text with blank-line paragraphs. Basic **bold** and *italics* markers are preserved as text.">
            <textarea data-testid="admin-legal-body-input" rows={16} className={inputCls} value={doc.body || ""} onChange={(e) => setDoc({ ...doc, body: e.target.value })} style={{ borderColor: "var(--border-cream)", resize: "vertical" }} />
          </Field>
          <button
            data-testid="admin-legal-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60"
            style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}
